import React, { ReactElement, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Select, SelectProps } from '@mui/material';
import Button from '../Button';
import { Flex } from '../Flex';
import DateRangeTableFilter from './TableFilters/DateRangeTableFilter';

const SelectStyled = styled(Select)<{
  $hasValue?: boolean;
}>`
  background: white;
  border-radius: 8px;
  border-color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.palette.common.black : 'auto'};

  color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.palette.common.black : 'auto'};

  &.MuiInputBase-root {
    opacity: 0 !important;
    width: 0 !important;
    display: none !important;
  }
`;

const SearchFieldContainer = styled(Flex)`
  width: 100%;
  height: 44px;
  position: sticky;
  top: 0;
  background: white;
  align-items: center;
  z-index: 2;
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray13};
`;

const ClearButton = styled(Button)`
  border-radius: 0;
  min-width: 91px;
  height: 100%;
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.common.gray8};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

type Props = Omit<SelectProps, 'onChange'> & {
  emptyValueLabel?: string;
  value?: string | string[];
  onClear?: () => void;
  onOpen?: () => void;
  menuButton: ReactElement;
  menuSelectedButton?: ReactElement;
  onChange?: (value: any) => void;
};

const ColumnDateRangeFiltersMenu = React.forwardRef(
  (
    {
      sx,
      value = [],
      onChange,
      onClear,
      onOpen,
      menuButton,
      menuSelectedButton,
      onClose,
      MenuProps,
      emptyValueLabel,
      ...props
    }: Props,
    ref,
  ) => {
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<any>(null);

    const [isOpen, setIsOpen] = useState(false);

    const hasValue = (value as string[]).length > 0;

    const handleMenuOpen = () => {
      setIsOpen(true);
      onOpen?.();
    };

    const menuProps = useMemo(() => {
      const mProps: any = {
        sx: { maxWidth: '520px', maxHeight: '400px', mt: 1 },
        MenuListProps: { disablePadding: true },
        PaperProps: {
          ref: optionsContainerRef,
          sx: {
            borderRadius: '8px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'common.gray13',
          },
        },
        ...MenuProps,
      };

      mProps.anchorEl = inputRef?.current;

      return mProps;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputRef?.current, MenuProps]);

    return (
      <>
        <Flex
          ref={inputRef}
          onClick={(e) => {
            e.stopPropagation();
            if (props.disabled) return;
            setIsOpen(true);
            handleMenuOpen();
          }}
        >
          {hasValue ? menuSelectedButton : menuButton}
        </Flex>

        <SelectStyled
          ref={ref}
          sx={sx}
          displayEmpty
          $hasValue={hasValue}
          open={isOpen}
          onOpen={handleMenuOpen}
          onClose={(e) => {
            e.stopPropagation();
            setIsOpen(false);
            onClose?.(e);
          }}
          MenuProps={menuProps}
          {...props}
        >
          <SearchFieldContainer divider={<Divider />}>
            <Box sx={{ padding: 2, width: '100%' }}>Select Date</Box>

            <ClearButton
              variant="contained"
              color="secondary"
              onMouseDown={() => {
                onChange?.([]);
                onClear?.();
              }}
              disabled={!hasValue}
            >
              Reset
            </ClearButton>
          </SearchFieldContainer>

          <DateRangeTableFilter
            emptyValueLabel={emptyValueLabel}
            range={value}
            onChange={onChange}
          />
        </SelectStyled>
      </>
    );
  },
);

export default ColumnDateRangeFiltersMenu;
